import React, { useState, useMemo, useRef } from 'react';
import { Truck, Plus, Edit, Trash2, Search, Save, X, Globe, Mail, Phone } from 'lucide-react';
import { useData } from '../context/DataContext';
import { useT } from '../context/LanguageContext';
import { toast } from 'sonner';
import { makeDirtyClose } from '../lib/useDirtyClose';
import { confirmDialog } from '../lib/confirm';

function LeverancierEditor({ leverancier, onClose }) {
  const { t } = useT();
  const { createLeverancier, updateLeverancier } = useData();
  const [data, setData] = useState(() => leverancier || {
    naam: '', contactpersoon: '', email: '', telefoon: '', klantnummer: '',
    betaaltermijn: 30, website: '', adres: '', notitie: '',
  });
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setData(d => ({ ...d, [k]: v }));
  const initialSig = useRef(null);
  if (initialSig.current === null) initialSig.current = JSON.stringify(data);
  const requestClose = makeDirtyClose(() => JSON.stringify(data) !== initialSig.current, onClose, t);

  const save = async () => {
    if (!data.naam.trim()) { toast.error(t('Naam is verplicht', 'Naam is verplicht')); return; }
    setSaving(true);
    try {
      if (leverancier?.id) await updateLeverancier(leverancier.id, data);
      else await createLeverancier(data);
      toast.success(t('Leverancier opgeslagen', 'Leverancier opgeslagen'));
      onClose();
    } catch (e) { toast.error(t('Mislukt'), { description: e.response?.data?.detail || e.message }); }
    finally { setSaving(false); }
  };

  return (
    <div className="cs-modal-overlay" onClick={requestClose}>
      <div className="cs-modal" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
        <div className="cs-modal-header">
          <h3><Truck size={16} /> {leverancier?.id ? t('Leverancier bewerken', 'Leverancier bewerken') : t('Nieuwe leverancier', 'Nieuwe leverancier')}</h3>
          <button className="cs-btn ghost" onClick={requestClose}><X size={16} /></button>
        </div>
        <div className="cs-modal-body" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div style={{ gridColumn: '1 / 3' }}><label className="cs-section-title">{t('Naam', 'Naam')} *</label>
            <input className="cs-input" value={data.naam} onChange={e => upd('naam', e.target.value)} data-testid="lev-naam" /></div>
          <div><label className="cs-section-title">{t('Contactpersoon', 'Contactpersoon')}</label>
            <input className="cs-input" value={data.contactpersoon ?? ''} onChange={e => upd('contactpersoon', e.target.value)} data-testid="lev-contact" /></div>
          <div><label className="cs-section-title">{t('Klantnummer', 'Klantnummer')}</label>
            <input className="cs-input" value={data.klantnummer ?? ''} onChange={e => upd('klantnummer', e.target.value)} data-testid="lev-klantnr" /></div>
          <div><label className="cs-section-title">E-mail</label>
            <input className="cs-input" type="email" value={data.email ?? ''} onChange={e => upd('email', e.target.value)} data-testid="lev-email" /></div>
          <div><label className="cs-section-title">{t('Telefoon', 'Telefoon')}</label>
            <input className="cs-input" value={data.telefoon ?? ''} onChange={e => upd('telefoon', e.target.value)} data-testid="lev-tel" /></div>
          <div><label className="cs-section-title">{t('Betaaltermijn (dagen)', 'Betaaltermijn (dagen)')}</label>
            <input className="cs-input" type="number" value={data.betaaltermijn ?? ''} onChange={e => upd('betaaltermijn', e.target.value)} data-testid="lev-termijn" /></div>
          <div><label className="cs-section-title">Website</label>
            <input className="cs-input" value={data.website ?? ''} onChange={e => upd('website', e.target.value)} data-testid="lev-website" /></div>
          <div style={{ gridColumn: '1 / 3' }}><label className="cs-section-title">{t('Adres', 'Adres')}</label>
            <input className="cs-input" value={data.adres} onChange={e => upd('adres', e.target.value)} /></div>
          <div style={{ gridColumn: '1 / 3' }}><label className="cs-section-title">{t('Notitie')}</label>
            <textarea className="cs-input" rows={2} value={data.notitie} onChange={e => upd('notitie', e.target.value)} /></div>
        </div>
        <div className="cs-modal-footer">
          <button className="cs-btn" onClick={requestClose}>{t('Annuleren')}</button>
          <button className="cs-btn primary" onClick={save} disabled={saving} data-testid="lev-save"><Save size={14} /> {t('Opslaan')}</button>
        </div>
      </div>
    </div>
  );
}

export default function Leveranciers() {
  const { t } = useT();
  const { leveranciers, inkoopOrders, deleteLeverancier } = useData();
  const [filter, setFilter] = useState('');
  const [editing, setEditing] = useState(null);

  const filtered = useMemo(() => {
    const f = filter.trim().toLowerCase();
    return (leveranciers || []).filter(l => !f || (l.naam || '').toLowerCase().includes(f) || (l.contactpersoon || '').toLowerCase().includes(f));
  }, [leveranciers, filter]);

  const orderCount = (lid) => (inkoopOrders || []).filter(o => o.leverancierId === lid).length;

  const remove = async (l) => {
    if (!(await confirmDialog(`${t('Leverancier verwijderen?', 'Leverancier verwijderen?')} ${l.naam}`))) return;
    try { await deleteLeverancier(l.id); toast.success(t('Leverancier verwijderd', 'Leverancier verwijderd')); }
    catch { toast.error(t('Mislukt')); }
  };

  return (
    <div>
      <div className="cs-page-header">
        <div><h2>{t('Leveranciers', 'Leveranciers')}</h2><p>{(leveranciers || []).length} {t('leveranciers', 'leveranciers')}</p></div>
        <button className="cs-btn primary" onClick={() => setEditing({})} data-testid="btn-lev-add"><Plus size={14} /> {t('Nieuwe leverancier', 'Nieuwe leverancier')}</button>
      </div>

      <div className="cs-toolbar">
        <div className="grow" style={{ position: 'relative' }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: 11, color: 'var(--cs-text-muted)' }} />
          <input className="cs-input" style={{ paddingLeft: 30 }} placeholder={t('Zoek leverancier...', 'Zoek leverancier...')} value={filter} onChange={e => setFilter(e.target.value)} />
        </div>
      </div>

      <div className="cs-card">
        <table className="cs-table">
          <thead><tr><th>{t('Naam', 'Naam')}</th><th>{t('Contactpersoon', 'Contactpersoon')}</th><th>{t('Contact', 'Contact')}</th><th>{t('Betaaltermijn', 'Betaaltermijn')}</th><th>{t('Orders', 'Orders')}</th><th></th></tr></thead>
          <tbody>
            {filtered.map(l => (
              <tr key={l.id} onDoubleClick={() => setEditing(l)} data-testid={`lev-row-${l.id}`}>
                <td style={{ fontWeight: 600 }}>{l.naam}</td>
                <td>{l.contactpersoon || '-'}</td>
                <td style={{ fontSize: 12 }}>
                  {l.email && <div><Mail size={11} /> {l.email}</div>}
                  {l.telefoon && <div><Phone size={11} /> {l.telefoon}</div>}
                  {l.website && <div><Globe size={11} /> {l.website}</div>}
                  {!l.email && !l.telefoon && !l.website && '-'}
                </td>
                <td>{l.betaaltermijn ? `${l.betaaltermijn} ${t('dagen', 'dagen')}` : '-'}</td>
                <td><span className="cs-badge blue">{orderCount(l.id)}</span></td>
                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <button className="cs-btn ghost" onClick={() => setEditing(l)} title={t('Bewerken')}><Edit size={12} /></button>
                  <button className="cs-btn ghost danger" onClick={() => remove(l)} data-testid={`lev-delete-${l.id}`} title={t('Verwijderen')}><Trash2 size={12} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <div className="cs-empty">{t('Nog geen leveranciers. Voeg je eerste leverancier toe.', 'Nog geen leveranciers. Voeg je eerste leverancier toe.')}</div>}
      </div>

      {editing && <LeverancierEditor leverancier={editing.id ? editing : null} onClose={() => setEditing(null)} />}
    </div>
  );
}
